import { useState } from "react";
import FileInfo from "files/types/interfaces/fileInfo";
import { Upload } from "lucide-react";
import { useStepperContext } from "~/components/ui/stepper";
import FileSelectShadcn from "./FileSelectShadcn";
import { StepHeader, ValidationJobCard, SummaryItem, StatCard } from "./SharedComponents";
import { ValidationJob, ColumnProfile } from "./types";

export const FileUploadStep = () => {
  const stepperContext = useStepperContext();
  const [localSelectedFile, setLocalSelectedFile] = useState<FileInfo | null>(null);

  if (!stepperContext) {
    return <div>Loading...</div>;
  }

  const { stepperState } = stepperContext;

  if (!stepperState) {
    return <div>Initializing...</div>;
  }

  const { uploadState, selectFromStorage, uploadError } = stepperState;

  if (!uploadState) {
    return <div>Initializing upload...</div>;
  }

  // Handle file selection from cloud storage
  const handleSelectFromStorage = (file: FileInfo) => {
    selectFromStorage(file);
  };

  // Determine which file to show as selected
  const displaySelectedFile = uploadState.selectedFile || localSelectedFile;

  return (
    <div className="space-y-4">
      <StepHeader
        title="Source File"
        subtitle="Upload a new file or select an existing file from cloud storage to validate. Supported formats: CSV and TSV"
      />

      {/* Show upload error if exists */}
      {uploadError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {uploadError}
        </div>
      )}

      {/* Show selected file info */}
      {displaySelectedFile && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">
            Selected: <span className="font-medium">{displaySelectedFile.key}</span>
          </p>
        </div>
      )}

      {/* File Upload Area - Placeholder for now */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 opacity-50">
        <Upload className="mx-auto mb-4 h-8 w-8 text-gray-400" />
        <p className="text-gray-700 mb-2">Drag and drop your file here or click to browse</p>
        <p className="text-sm text-gray-500">Maximum file size: 10MB</p>
        <p className="text-xs text-gray-400 mt-2">(File upload coming soon)</p>

        {/* Show upload progress */}
        {uploadState.isUploading && uploadState.uploadProgress !== undefined && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadState.uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-2">{uploadState.uploadProgress}% uploaded</p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or</span>
        </div>
      </div>

      {/* Cloud Storage Section */}
      <div>
        <h4 className="text-base font-medium mb-3">Select from Cloud Storage</h4>
        <FileSelectShadcn
          selectedFile={localSelectedFile}
          setSelectedFile={setLocalSelectedFile}
          onSelectFile={handleSelectFromStorage}
          placeholder="Select source file..."
        />
      </div>
    </div>
  );
};

export const FileValidationStep = () => {
  const stepperContext = useStepperContext();

  if (!stepperContext) {
    console.error("FileValidationStep: useStepperContext returned null");
    return <div>Loading...</div>;
  }

  const { stepperState } = stepperContext;

  if (!stepperState) {
    console.error("FileValidationStep: stepperState is undefined");
    return <div>Initializing...</div>;
  }

  const { validationState, isValidating, uploadState } = stepperState;

  if (!validationState) {
    console.error("FileValidationStep: validationState is undefined");
    return <div>Initializing validation...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <StepHeader title="File Validation" />
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Validating file:</span>
          <span className="font-medium">
            {uploadState?.selectedFile?.key || "Clinical_Data_Discovery_Cohort.csv"}
          </span>
        </div>
      </div>

      {/* Show loading state */}
      {isValidating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">Validation in progress...</p>
        </div>
      )}

      {/* Validation Jobs */}
      <div className="space-y-4">
        {validationState.jobs.map((job: ValidationJob, index: number) => (
          <ValidationJobCard key={index} {...job} />
        ))}
      </div>

      {/* Review Validation Summary */}
      {validationState.isComplete && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-3">Review Validation Summary</h4>
          <div className="space-y-2 text-sm">
            <SummaryItem label="Total Rows in File" value={validationState.summary.totalRows} />
            <SummaryItem
              label="Header Row"
              value={validationState.summary.headerRow ? "yes" : "no"}
            />
            <SummaryItem label="Data Rows" value={validationState.summary.dataRows} />
            <SummaryItem label="Data Rows with Errors" value={validationState.summary.errorRows} />
            <SummaryItem label="Clean Data Rows" value={validationState.summary.cleanRows} />
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-700 font-medium">
              All {validationState.summary.cleanRows} data rows validated successfully!
            </p>
            <p className="text-sm text-gray-600">
              No errors detected. Ready to proceed with data profiling.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const SummaryStep = () => {
  const stepperContext = useStepperContext();

  if (!stepperContext) {
    console.error("SummaryStep: useStepperContext returned null");
    return <div>Loading...</div>;
  }

  const { stepperState } = stepperContext;

  if (!stepperState) {
    console.error("SummaryStep: stepperState is undefined");
    return <div>Initializing...</div>;
  }

  const { dataProfile, isProfiling, uploadState } = stepperState;

  // Show loading state
  if (isProfiling) {
    return (
      <div className="space-y-6">
        <StepHeader title="Column Summary" />
        <div className="p-8 text-center">
          <p className="text-gray-600">Analyzing data profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if no profile
  if (!dataProfile) {
    return (
      <div className="space-y-6">
        <StepHeader title="Column Summary" />
        <div className="p-8 text-center">
          <p className="text-gray-600">No data profile available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <StepHeader
          title="Column Summary"
          subtitle="We have done a data profile on the data you have provided. Please review the profile we discovered from your data. It will be used to create the target tables in the database."
        />
        <div className="flex items-center gap-2 text-sm mt-4">
          <span className="text-gray-600">File:</span>
          <span className="font-medium">
            {uploadState?.selectedFile?.key || "Clinical_Data_Discovery_Cohort.csv"}
          </span>
        </div>
      </div>

      {/* Column Summary Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-700">
            <span>Column Name</span>
            <span>Data Type</span>
            <span>Categorical</span>
            <span>Null %</span>
          </div>
        </div>

        <div className="divide-y">
          {dataProfile.columns.map((column: ColumnProfile, index: number) => (
            <div key={index} className="grid grid-cols-4 gap-4 p-4 text-sm hover:bg-gray-50">
              <span className="font-medium">{column.name}</span>
              <span>{column.type}</span>
              <span>{column.categorical ? "yes" : "no"}</span>
              <span>{column.nullPercent}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Columns" value={dataProfile.statistics.totalColumns} color="gray" />
        <StatCard
          label="Categorical Columns"
          value={dataProfile.statistics.categoricalColumns}
          color="blue"
        />
        <StatCard label="Good Rows" value={dataProfile.statistics.goodRows} color="green" />
        <StatCard label="Error Rows" value={dataProfile.statistics.errorRows} color="red" />
      </div>
    </div>
  );
};
